import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import {
  FaHome,
  FaUserCheck,
  FaUsers,
  FaUserFriends,
  FaFileContract,
  FaEnvelope,
  FaBell,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaShieldAlt,
  FaIdBadge,
  FaClipboardList,
  FaChalkboardTeacher,
  FaStore,
  FaChartBar,
  FaCheckCircle,
  FaDollarSign,
} from 'react-icons/fa';
import logo from '../images/logo.png';
import UserContext from './UserContext';
import Logout from './Logout';
import Footer from './Footer';

function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { session: admin, setSession } = useContext(UserContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle sidebar toggle for mobile
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Navigation items
  const navigationItems = [
    {
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: FaHome,
      exact: true,
    },
    {
      name: 'Pending Approvals',
      path: '/admin/pending-approvals',
      icon: FaUserCheck,
    },
    {
      name: 'Business Partners',
      path: '/admin/trainers',
      icon: FaUsers,
    },
    {
      name: 'Members',
      path: '/admin/users',
      icon: FaUserFriends,
    },
    {
      name: 'Students',
      path: '/admin/students',
      icon: FaUserFriends,
    },
    {
      name: 'Programs',
      path: '/admin/programs',
      icon: FaClipboardList,
    },
    {
      name: 'Classes',
      path: '/admin/classes',
      icon: FaChalkboardTeacher,
    },
    {
      name: 'Legal Documents',
      path: '/admin/legal-documents',
      icon: FaFileContract,
    },
    {
      name: 'Newsletter',
      path: '/admin/newsletter',
      icon: FaEnvelope,
    },
    {
      name: 'Notifications',
      path: '/admin/notifications',
      icon: FaBell,
    },
    // Marketplace Section
    {
      name: 'Marketplace Overview',
      path: '/admin/marketplace',
      icon: FaChartBar,
    },
    {
      name: 'Reseller Approvals',
      path: '/admin/reseller-approvals',
      icon: FaUserCheck,
    },
    {
      name: 'Product Approvals',
      path: '/admin/product-approvals',
      icon: FaCheckCircle,
    },
    {
      name: 'Commission Reports',
      path: '/admin/marketplace/commission-report',
      icon: FaDollarSign,
    },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    Logout(navigate, setSession);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky lg:top-0 left-0 z-50
            w-64 bg-gradient-to-b from-purple-700 to-indigo-800 text-white
            transform transition-transform duration-300 ease-in-out
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            flex flex-col
            shadow-xl
            h-screen lg:h-auto lg:self-stretch lg:overflow-y-auto
          `}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/20">
            <Link to="/admin/dashboard" className="flex items-center space-x-3">
              <img src={logo} alt="Yogvital Logo" className="h-10 w-auto" />
              <div>
                <h1 className="text-lg font-bold">Yogvital</h1>
                <p className="text-xs text-white/80">Admin Panel</p>
              </div>
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden text-white hover:text-yellow-300 transition-colors"
              aria-label="Close sidebar"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-2 space-y-1">
              {navigationItems.map((item, index) => {
                const Icon = item.icon;
                const active = isActive(item.path, item.exact);
                    const isMarketplaceStart = item.name === 'Marketplace Overview';
                
                return (
                  <React.Fragment key={item.path}>
                    {/* Marketplace Section Heading */}
                    {isMarketplaceStart && (
                      <div className="px-4 py-2 mt-6 mb-2 border-t border-white/10 pt-6">
                        <h3 className="text-white text-sm font-bold uppercase tracking-widest">
                          Marketplace
                        </h3>
                      </div>
                    )}
                    <Link
                      to={item.path}
                      className={`
                        flex items-center px-4 py-3 rounded-lg transition-all duration-200
                        ${active
                          ? 'bg-white/20 text-white shadow-md'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }
                      `}
                    >
                      <Icon className={`mr-3 ${active ? 'text-yellow-300' : ''}`} />
                      <span className="font-medium">{item.name}</span>
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </nav>

          {/* User Info & Logout */}
          <div className="border-t border-white/20 p-4 space-y-3">
            {/* User Info */}
            <div className="flex items-center space-x-3 px-2 py-2 bg-white/10 rounded-lg">
              <div className="relative">
                <FaShieldAlt className="w-8 h-8 text-white" />
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate capitalize">
                  {admin?.name || 'Admin'}
                </p>
                <p className="text-xs text-white/70 truncate">{admin?.email || ''}</p>
                <div className="flex items-center mt-1">
                  <FaIdBadge className="text-xs text-white/60 mr-1" />
                  <span className="text-xs text-white/60 font-mono">
                    {admin?.id?.toString().slice(-6) || ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 font-medium"
            >
              <FaSignOutAlt className="mr-2" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Header Bar */}
          <header className="bg-white shadow-sm sticky top-0 z-30">
            <div className="flex items-center justify-between px-4 py-3">
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden text-gray-600 hover:text-gray-900 p-2 rounded-md hover:bg-gray-100"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <FaTimes className="text-xl" />
                ) : (
                  <FaBars className="text-xl" />
                )}
              </button>

              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  {navigationItems.find(item => isActive(item.path, item.exact))?.name || 'Admin Dashboard'}
                </h2>
              </div>

              {/* Desktop User Info (minimal) */}
              <div className="hidden lg:flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {admin?.name || 'Admin'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </p>
                </div>
                <div className="relative">
                  <FaShieldAlt className="w-10 h-10 text-purple-600" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>

      {/* Footer - Full Width */}
      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
}

export default AdminLayout;

